/**
 * Email Signup Block Component
 *
 * Handles dynamic padding adjustment for email signup input fields
 * when integrated buttons have variable widths.
 * Also handles saving and restoring email value on form submission.
 */
class EmailSignupBlock extends HTMLElement {
  constructor() {
    super();
    /** @type {number} */
    this.lastButtonWidth = 0;
    /** @type {ResizeObserver | undefined} */
    this.observer = undefined;

    this.adjustInputPadding = this.adjustInputPadding.bind(this);
    this.handleFormSubmit = this.handleFormSubmit.bind(this);
    this.restoreEmailValue = this.restoreEmailValue.bind(this);
  }

  connectedCallback() {
    this.form = this.querySelector('form');
    this.button = this.querySelector('.email-signup__button');
    /** @type {HTMLInputElement | null} */
    this.input = this.querySelector('.email-signup__input');
    this.inputGroup = this.querySelector('.email-signup__input-group');
    this.isTextButton = this.button && this.button.classList.contains('email-signup__button--text');

    if (this.button && this.input && this.inputGroup && this.isTextButton) {
      this.adjustInputPadding();

      this.observer = new ResizeObserver(() => {
        this.adjustInputPadding();
      });
      this.observer.observe(this.button);

      if ('fonts' in document) {
        document.fonts.ready.then(() => {
          this.adjustInputPadding();
        });
      }
    }

    if (this.form && this.input) {
      this.form.addEventListener('submit', this.handleFormSubmit);
      this.restoreEmailValue();
    }
  }

  disconnectedCallback() {
    if (this.observer) {
      this.observer.disconnect();
    }

    if (this.form) {
      this.form.removeEventListener('submit', this.handleFormSubmit);
    }
  }

  handleFormSubmit() {
    if (this.input && this.input.value) {
      const formId = this.form?.id || 'email-signup-form';
      const sessionStorageKey = `${formId}-email`;
      sessionStorage.setItem(sessionStorageKey, this.input.value);
    }
  }

  restoreEmailValue() {
    if (!this.input || !this.form) return;

    const formId = this.form.id || 'email-signup-form';
    const formIdFromUrl = window.location.hash.replace('#', '');

    if (formIdFromUrl !== formId) return;

    const sessionStorageKey = `${formId}-email`;
    let emailToRestore = '';

    const urlParams = new URLSearchParams(window.location.search);

    const isFormSuccessfullyPosted = 
      urlParams.get('customer_posted') === 'true'
      || urlParams.get('form_type') === 'customer'
      || urlParams.get('posted_successfully') === 'true'
    ;

    if (!isFormSuccessfullyPosted) {
      sessionStorage.removeItem(sessionStorageKey);
      return;
    }

    if (urlParams.has('customer[email]')) {
      emailToRestore = urlParams.get('customer[email]') || '';
    } else {
      emailToRestore = sessionStorage.getItem(sessionStorageKey) || '';
    }

    if (emailToRestore !== '') {
      this.input.value = emailToRestore;
    }

    if (this.button && this.isTextButton && this.button.dataset.successText) {
      this.input.readOnly = true;
      this.button.textContent = this.button.dataset.successText;
      this.button.classList.add('button--success');
      this.button.disabled = true;
    }

    this.setAttribute('success', '');
  }

  adjustInputPadding() {
    if (!this.button || !this.inputGroup) return;

    const buttonWidth = /** @type {HTMLElement} */ (this.button).offsetWidth;

    if (buttonWidth !== this.lastButtonWidth) {
      this.lastButtonWidth = buttonWidth;
      /** @type {HTMLElement} */ (this.inputGroup).style.setProperty('--button-actual-width', `${buttonWidth}px`);
    }
  }
}

if (!customElements.get('email-signup-block')) {
  customElements.define('email-signup-block', EmailSignupBlock);
}
